import { TextInput } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';

import { CoverageAreaEstimates } from 'types';

interface ForecastVolumeTableProps {
  estimates: CoverageAreaEstimates;
  isEditing: boolean;
  onChange: (estimates: CoverageAreaEstimates) => void;
}

interface MonthEntry {
  dt: DateTime;
  year: number;
  paddedMonth: string;
  label: string;
}

function buildMonths(): MonthEntry[] {
  return Array.from({ length: 12 }, (_, i) => {
    const dt = DateTime.now().startOf('month').plus({ months: i });
    const year = dt.year;
    const paddedMonth = String(dt.month).padStart(2, '0');
    const label = dt.toFormat('MMMM, yyyy');
    return { dt, year, paddedMonth, label };
  });
}

interface MonthRowProps {
  entry: MonthEntry;
  value: number;
  isEditing: boolean;
  isLast: boolean;
  onChange: (year: number, paddedMonth: string, value: number) => void;
}

function MonthRow({ entry, value, isEditing, isLast, onChange }: MonthRowProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const numVal = parseInt(e.target.value, 10);
      onChange(entry.year, entry.paddedMonth, isNaN(numVal) ? 0 : Math.max(0, numVal));
    },
    [entry.year, entry.paddedMonth, onChange],
  );

  const inputProps = useMemo(() => ({ readOnly: !isEditing }), [isEditing]);

  return (
    <div
      className={`flex items-center justify-between py-2${isLast ? '' : ' border-b [border-color:var(--color-border-separator)]'}`}
    >
      <span className="text-xs font-normal">{entry.label}</span>
      <div className="w-[104px]">
        <TextInput
          id={`forecast-${entry.year}-${entry.paddedMonth}`}
          name={`forecast-${entry.year}-${entry.paddedMonth}`}
          type="number"
          min={0}
          value={String(value)}
          onChange={handleChange}
          inputProps={inputProps}
          label=""
          hideLabel
        />
      </div>
    </div>
  );
}

export function ForecastVolumeTable({ estimates, isEditing, onChange }: ForecastVolumeTableProps) {
  const months = buildMonths();
  const leftMonths = months.slice(0, 6);
  const rightMonths = months.slice(6, 12);

  const getValue = useCallback(
    (year: number, paddedMonth: string): number => {
      return estimates?.[year]?.[paddedMonth] ?? 0;
    },
    [estimates],
  );

  const handleChange = useCallback(
    (year: number, paddedMonth: string, value: number) => {
      const updatedYear = { ...(estimates?.[year] ?? {}), [paddedMonth]: value };
      onChange({ ...estimates, [year]: updatedYear });
    },
    [estimates, onChange],
  );

  const renderColumn = useCallback(
    (columnMonths: MonthEntry[]) => (
      <div className="flex-1">
        <div className="py-1 flex items-center justify-between border-b [border-color:var(--color-border-separator)]">
          <span className="text-xs font-bold">Month</span>
          <span className="text-xs font-bold">Forecast Volume</span>
        </div>
        {columnMonths.map((entry, idx) => (
          <MonthRow
            key={`${entry.year}-${entry.paddedMonth}`}
            entry={entry}
            value={getValue(entry.year, entry.paddedMonth)}
            isEditing={isEditing}
            isLast={idx === columnMonths.length - 1}
            onChange={handleChange}
          />
        ))}
      </div>
    ),
    [getValue, isEditing, handleChange],
  );

  return (
    <div className="flex gap-4">
      {renderColumn(leftMonths)}
      {renderColumn(rightMonths)}
    </div>
  );
}
